// target-discount.ts — General discount entered as a TARGET PRICE (book 4.10 §5).
//
// NOT a fifth discount mechanism: the book keeps four (agency, tier, line, general). This is a SECOND
// ENTRY MODE for the existing general discount field — type the wanted total instead of the rate, and
// the rate is derived. The cascade order does not move: the rush still comes after the discount.
//
// THE TARGET IS THE FINAL TOTAL, RUSH INCLUDED — the price the client will see. Since the rush applies
// AFTER the discount (and, when priced, as a PERCENTAGE of the already-discounted base), it moves with
// the discount, so resolving the target means inverting the rush formula, not just subtracting.
//
// THE AMOUNT IS THE RESULT, NOT A SUM. R is computed once, globally, so the final total equals the
// target TO THE CENT by construction. The per-line split (see `allocateDiscount`) is INTERNAL and its
// rounding never affects the total.

import { round2 } from "../core/core";
import { engineMessage } from "../core/messages";

/** Entry mode of the general discount field. */
export type GeneralDiscountMode = "rate" | "target";

/** The rush as it will apply to the discounted base (mirror of `rush()` in discounts.ts). */
export interface RushTarget {
  /** NEGOTIATED flat fee: fixed entered amount, insensitive to the base — it simply adds up. */
  negotiated?: boolean;
  /** Amount of the negotiated flat fee. */
  negotiatedAmount?: number;
  /** PRICED rush: percentage of the discounted base (0.25 = +25 %). */
  pct?: number;
  /** PRICED rush: € floor — the rush never goes below it. */
  floor?: number;
}

/** Input of the target-price resolution. */
export interface TargetEntry {
  /** Total BEFORE discount (print + services), € excl. VAT. */
  totalBeforeDiscount: number;
  /**
   * MAXIMUM reachable discount, € — the one a 100 % rate would give. It carries all the knowledge of
   * the floors: with the minima box ticked it equals `totalBeforeDiscount` (nothing protects any more),
   * unticked it stops at the sum of the minimum unit price floors. Received already computed rather
   * than re-deriving the floors here, since a second implementation would eventually diverge.
   */
  maxDiscount: number;
  /** Requested target: final total excl. VAT, rush included. */
  target: number;
  /** The quote's rush. Absent = no rush. */
  rush?: RushTarget | undefined;
}

/** Resolution branch taken — traced so the calculation stays re-readable. */
export type TargetBranch = "no-rush" | "rush-negotiated" | "rush-percentage" | "rush-floor";

/** Outcome of the target-price resolution: the discount amount, or the reason it is refused. */
export type TargetResolution =
  | {
      ok: true;
      amount: number;
      branch: TargetBranch;
      /**
       * Final total actually obtained. Equal to the target TO THE CENT in every case but one: with a
       * PRICED percentage rush, the rush is itself rounded to the cent, which leaves GAPS in the
       * reachable totals (measured on a +25 % rush: five totals out of twenty-one are impossible,
       * whatever the discount amount). The nearest reachable cent is used (gap ≤ 1 cent) and REPORTED,
       * rather than pretending the target was hit.
       */
      reachedTotal: number;
      /** True when `reachedTotal` differs from the target — the gap comes from the rush rounding. */
      roundedByRush?: boolean;
    }
  /** The target exceeds the quote at full price: that would be a disguised MARKUP. Refused. */
  | { ok: false; reason: "target-too-high"; totalMax: number }
  /** The target falls below the quote floor (minima protection). Refused, with the floor. */
  | { ok: false; reason: "target-unreachable"; floor: number }
  /** No coherent rush branch — should not happen; reported rather than approximated. */
  | { ok: false; reason: "no-branch" };

/**
 * Rush amount for a given discounted base (identical to `rush()` in discounts.ts).
 * @param base The discounted base.
 * @param r The rush, if any.
 * @returns The rush amount, rounded to the cent.
 */
export function rushOnBase(base: number, r: RushTarget | undefined): number {
  if (!r) return 0;
  if (r.negotiated) return round2(r.negotiatedAmount ?? 0);
  const pct = r.pct ?? 0;
  const floor = r.floor ?? 0;
  if (pct <= 0 && floor <= 0) return 0;
  return round2(Math.max(floor, base * pct));
}

/**
 * Final total for a given discounted base: base + rush.
 * @param base The discounted base.
 * @param r The rush, if any.
 * @returns The final total, rounded to the cent.
 */
export function finalTotalFor(base: number, r: RushTarget | undefined): number {
  return round2(base + rushOnBase(base, r));
}

/**
 * MAXIMUM total of the quote: no discount at all. A target above it is refused.
 * @param e The target entry.
 * @returns The maximum final total.
 */
export function quoteMaxTotal(e: TargetEntry): number {
  return finalTotalFor(round2(e.totalBeforeDiscount), e.rush);
}

/**
 * ABSOLUTE FLOOR of the quote: the lowest final total reachable without lifting the minima protection.
 * With the box unticked, the minimum unit price floors bound the descent — going lower requires ticking
 * the box or excluding lines. Computed BEFORE any resolution, so the tool never lands at €29,400 when
 * asked for €28,000.
 * @param e The target entry.
 * @returns The lowest reachable final total.
 */
export function quoteFloor(e: TargetEntry): number {
  const baseMin = round2(Math.max(0, e.totalBeforeDiscount - e.maxDiscount));
  return finalTotalFor(baseMin, e.rush);
}

/**
 * Resolves the discount amount R that lands the final total EXACTLY on the target.
 *
 * No rush ................ R = A − C
 * Negotiated rush (fixed m) R = A + m − C
 * Priced rush (pct k, floor p): two branches, resolved THEN validated —
 *   · percentage: R = A − C/(1+k), valid if (A−R) × k ≥ p (the floor does not bite)
 *   · floor .....: R = A + p − C, valid if (A−R) × k < p (the floor bites)
 * Both cannot hold at once; if neither does, say so instead of rounding.
 * @param e The target entry.
 * @returns The resolved discount amount, or the reason the target is refused.
 */
export function resolveTargetPrice(e: TargetEntry): TargetResolution {
  const A = round2(e.totalBeforeDiscount);
  const C = round2(e.target);

  const totalMax = quoteMaxTotal(e);
  if (C > totalMax) return { ok: false, reason: "target-too-high", totalMax };

  const quoteFloorTotal = quoteFloor(e);
  if (C < quoteFloorTotal) return { ok: false, reason: "target-unreachable", floor: quoteFloorTotal };

  const r = e.rush;
  if (!r) return snapToCent(A, C, undefined, round2(A - C), "no-rush");
  if (r.negotiated) return snapToCent(A, C, r, round2(A + round2(r.negotiatedAmount ?? 0) - C), "rush-negotiated");

  const pct = r.pct ?? 0;
  const p = r.floor ?? 0;
  if (pct <= 0 && p <= 0) return snapToCent(A, C, r, round2(A - C), "no-rush");

  // PERCENTAGE branch — the rush follows the discounted base.
  const rPct = round2(A - C / (1 + pct));
  if (round2((A - rPct) * pct) >= p) return snapToCent(A, C, r, rPct, "rush-percentage");

  // FLOOR branch — the rush is stuck at its minimum and no longer moves with the base.
  const rFloor = round2(A + p - C);
  if (round2((A - rFloor) * pct) < p) return snapToCent(A, C, r, rFloor, "rush-floor");

  return { ok: false, reason: "no-branch" };
}

/**
 * Snaps the analytic solution to the cent: it is algebraically right, but the successive roundings
 * (discount, then rush, then total) can shift it by a cent or two. A small window around it is swept and
 * the amount that hits the target is kept; failing that — the GAPS of the priced rush, see
 * `reachedTotal` — the closest one, flagged.
 * @param A The total before discount.
 * @param C The target total.
 * @param r The rush, if any.
 * @param start The analytic solution.
 * @param branch The resolution branch taken.
 * @returns The resolution, with the amount that lands closest to the target.
 */
function snapToCent(A: number, C: number, r: RushTarget | undefined, start: number, branch: TargetBranch): TargetResolution {
  let best = start;
  let bestTotal = finalTotalFor(round2(A - start), r);
  let bestGap = Math.abs(round2(bestTotal - C));
  for (let cents = -8; cents <= 8 && bestGap > 0; cents++) {
    const m = round2(start + cents / 100);
    if (m < 0) continue;
    const total = finalTotalFor(round2(A - m), r);
    const gap = Math.abs(round2(total - C));
    if (gap < bestGap) { best = m; bestTotal = total; bestGap = gap; }
  }
  return bestGap === 0
    ? { ok: true, amount: best, branch, reachedTotal: bestTotal }
    : { ok: true, amount: best, branch, reachedTotal: bestTotal, roundedByRush: true };
}

/**
 * Business message for a refusal, as an engine message (displayed on screen, never a silent approximation).
 * @param r The refused resolution.
 * @returns The encoded message, translated by the display layer.
 */
export function refusalMessage(r: Extract<TargetResolution, { ok: false }>): string {
  switch (r.reason) {
    case "target-too-high":
      return engineMessage("engineTargetTooHigh", { maxEur: r.totalMax });
    case "target-unreachable":
      return engineMessage("engineTargetUnreachable", { floorEur: r.floor });
    case "no-branch":
      return "engineTargetNoBranch";
  }
}
